/*
 * The manual, written for the developer running a job on this system.
 *
 * Kept as data rather than markup so the search index and the page are built
 * from the same source, and so a screen cannot quietly go undocumented without
 * docs-check noticing.
 *
 * Every entry names the screen it describes with `covers`, which is the nav key
 * from NAV in app.jsx. The check asserts the two lists match.
 */

export const DOCS = [
  {
    id: "start",
    title: "Getting started",
    summary: "What the system is for, and the first hour on a new job.",
    sections: [
      {
        id: "what-it-is",
        heading: "What this is",
        blocks: [
          { p: "This is a control system for construction draws. It holds the schedule of values, tracks what has been built, collects the signatures that have to exist before money moves, and assembles the payment application at the end of that process." },
          { p: "The rule the whole system is built around is short. No line item reaches a payment application until every party who has to stand behind it has signed for it. Money is the last step, and no step before it can be skipped or reordered." },
          { p: "That rule is enforced by the server, not by convention. If a step is missing, the request is refused and the reason is given. You cannot click past it, and neither can anyone you invite." },
        ],
      },
      {
        id: "first-hour",
        heading: "The first hour on a new job",
        blocks: [
          { p: "Work in this order. Each step depends on the one before it." },
          {
            steps: [
              "Project setup. Name, address, timezone, contract sum, retainage, delay penalty. The timezone matters because penalties accrue per calendar day at the site.",
              "Import the schedule of values, or enter it by hand. Import is faster and the CSV format is documented under Import and export.",
              "Add the parties. A party is a company. Add the general contractor, the subs you know about, the inspector, the lender, the architect.",
              "Offer scope. Each line item gets assigned to the party responsible for it. They accept, and the terms freeze at that moment.",
              "Invite people. An invitation goes to a person at a party. Send them once the conversation has already happened, not before.",
            ],
          },
          { note: "The system will not let you prepare a payment application until retainage and the delay penalty have been confirmed. Zero is a valid answer for either. What it refuses is a project reaching its first draw with terms nobody looked at." },
        ],
      },
      {
        id: "vocabulary",
        heading: "Words that mean something specific here",
        blocks: [
          {
            table: {
              head: ["Term", "What it means in this system"],
              rows: [
                ["Party", "A company. What appears on a signature stamp and what holds scope."],
                ["User", "A person who signs in. A user can belong to several projects."],
                ["Membership", "The link between a user and one project, carrying their role. Roles are per project, never global."],
                ["Line item", "One row of the schedule of values, with its own value, dates, stage and assignment."],
                ["Assignment", "The offer of a line item to a party, and their acceptance of it. Separate from the line item itself."],
                ["Value in place", "The dollar amount actually built. Stored directly. Percent is derived from it, not the other way round."],
                ["Draw", "A payment application. Numbered within the project, continuing the job's own numbering."],
              ],
            },
          },
        ],
      },
    ],
  },

  {
    id: "roles",
    title: "Roles and permissions",
    summary: "Who can do what, why the boundaries sit where they do, and how to grant more.",
    sections: [
      {
        id: "the-roles",
        heading: "The six roles",
        blocks: [
          {
            table: {
              head: ["Role", "What they do"],
              rows: [
                ["Owner / Developer", "Runs the job. Sets scope and commercial terms, approves work, raises change orders, manages parties and settings."],
                ["Partner / Staff", "Works alongside the owner. Starts with read access, and receives specific capabilities one at a time."],
                ["Contractor", "Holds scope. Starts and submits the work assigned to them, posts photos and daily logs, and can move percent on their own line items and nothing else."],
                ["Inspector", "Verifies work. Can raise punch items. Cannot approve, and cannot release money."],
                ["Lender", "Sees the whole job, receives draw requests, releases or returns them, and attaches proof of payment. Cannot start or approve work."],
                ["Investor", "Sees capital status and the published briefing. Sees no line item, no draw, no contractor and no audit row."],
              ],
            },
          },
          { p: "Roles are attached to a membership, so the same person can be an owner on one project and an investor on another." },
        ],
      },
      {
        id: "field-level",
        heading: "Permission is by field, not by screen",
        blocks: [
          { p: "Hiding a button is not a permission. A contractor who sends a request directly is refused by the server, field by field. They may write percent on a line item they hold. Every other field on that row is rejected, and the response says which ones were refused." },
          { p: "The same applies to reads. The investor view is assembled by a separate handler that only ever builds capital data. It is not the project response with fields removed, because a response with fields removed is one mistake away from a response with fields." },
        ],
      },
      {
        id: "grants",
        heading: "Giving a partner more to do",
        blocks: [
          { p: "Partners start with visibility and nothing else. On the Contractors and subs screen, open a partner and grant capabilities one at a time. Each grant is additive and recorded." },
          { p: "Some capabilities are not delegable at all. Releasing funds, marking a draw paid and changing project settings stay with the roles that hold them, whatever is granted. Attempting to delegate one has no effect rather than a silent success." },
        ],
      },
    ],
  },

  {
    id: "work",
    title: "The work",
    summary: "Overview, schedule of values, timeline, daily log, punch list, suppliers.",
    covers: ["dashboard", "ledger", "timeline", "daily", "punch", "supply"],
    sections: [
      {
        id: "overview",
        heading: "Overview",
        blocks: [
          { p: "The first screen after sign in. It shows the contract sum, what has been built, what has been disbursed, and what is waiting on somebody." },
          { p: "The alert list underneath is derived every time the page loads. It is never stored, because a stored alert outlives the thing that caused it and then lies. If an alert is on screen, the condition is true right now." },
        ],
      },
      {
        id: "ledger",
        heading: "Schedule of values",
        blocks: [
          { p: "The centre of the system. One row per line item, showing its number, name, scheduled value, value in place, stage, assignment and signatures." },
          { p: "Click a row to open it. Inside are the photos, the approvals, the notes, the punch items and the assignment. Everything about that piece of work is on that one panel." },
          { p: "Value in place is what you edit. The percentage is calculated from it. This is the opposite of most spreadsheets and it is deliberate: the G703 defines the percentage as value in place divided by scheduled value, and recalculating dollars from a rounded percentage manufactured over two thousand dollars of phantom funding on a real job." },
          {
            heading2: "The stages",
            list: [
              "Not started. Nothing has happened yet.",
              "In progress. Work has begun. A photo is required to get here.",
              "Submitted. The contractor says it is complete. A second photo is required.",
              "Verified. The inspector has looked at it.",
              "Approved. The owner accepts it.",
              "Released. Every required signature is on file and it may go into a draw.",
              "Paid. Money has moved against it.",
            ],
          },
          { p: "From any middle stage the work can be rejected, which returns it. The transition allowed to you depends on your role, and each one has a precondition the server checks before it happens." },
        ],
      },
      {
        id: "timeline",
        heading: "Timeline",
        blocks: [
          { p: "The schedule of values arranged by date rather than by number. Planned finish dates, actual progress, and anything running late." },
          { p: "Lateness here is measured against the finish date the party accepted, not against a date you changed afterwards. That distinction is what makes a delay penalty defensible." },
        ],
      },
      {
        id: "daily",
        heading: "Daily log",
        blocks: [
          { p: "One entry per day, per party. Crew count, hours, a summary, blockers, and photos." },
          { p: "The log is the ordinary record of the job, and it is also what makes a delay argument survivable months later. A penalty backed by daily entries showing nobody on site is a different conversation from a penalty backed by a date alone." },
        ],
      },
      {
        id: "punch",
        heading: "Punch list",
        blocks: [
          { p: "Deficiencies raised against a line item. Severity is not a label here. It decides what happens to money." },
          {
            table: {
              head: ["Severity", "Effect"],
              rows: [
                ["Blocking", "Approve, verify and release are all refused while it is open."],
                ["Holdback", "A named amount is withheld on top of retainage until it is verified."],
                ["Minor", "Recorded. No effect on money."],
              ],
            },
          },
          { p: "Punch items are waived with a note, never deleted, and the waiver writes an audit row naming who did it." },
        ],
      },
      {
        id: "supply",
        heading: "Suppliers and materials",
        blocks: [
          { p: "Long lead items and who supplies them. Each entry carries a lead time and the date it is needed on site." },
          { p: "The order by date is calculated from those two, never stored. Change the need date and the order date moves with it, which is the only behaviour that stays correct as a schedule slips." },
        ],
      },
    ],
  },

  {
    id: "money",
    title: "Money",
    summary: "Draw requests, change orders, budget reconciliation, exceptions.",
    covers: ["draws", "changeorders", "reconcile", "exceptions"],
    sections: [
      {
        id: "draws",
        heading: "Draw requests",
        blocks: [
          { p: "A draw is a payment application. Select the released line items to bill, and the system assembles the request." },
          {
            heading2: "How the amount is calculated",
            p: "Net due is value in place, less retainage, less what has already been paid, less any delay penalties, less the total of every open holdback punch item.",
          },
          { p: "That calculation is frozen as a snapshot the moment the draw is submitted. If the bank short pays, the difference is reconciled against what was actually asked for rather than against a number that has moved since." },
          { p: "Allocation across line items uses largest remainder, so the parts sum to the payment exactly. Rounding each share independently drifts a dollar or two per draw, and a dollar of unexplained drift at closeout costs more trust than it is worth." },
          { p: "The lender releases or returns the draw and attaches proof of payment. Documents attached to a draw are evidence, so the original file is stored untouched with its hash recorded." },
        ],
      },
      {
        id: "changeorders",
        heading: "Change orders",
        blocks: [
          { p: "A change order adjusts scope, value or dates on a line item after somebody has already accepted it." },
          { p: "Terms freeze at acceptance. When the live line item drifts from those frozen terms, the difference surfaces here rather than quietly becoming the new normal. The party has to accept the change before it binds them, and until they do, the old terms are what governs." },
          { p: "Each change order names the line item it affects. Clicking through takes you to that row in the schedule of values." },
        ],
      },
      {
        id: "reconcile",
        heading: "Budget vs schedule of values",
        blocks: [
          { p: "The budget and the schedule of values are two different documents that are supposed to agree. This screen reports where they do not, by division." },
          { p: "On the job this system was built against, that comparison found eighty five thousand dollars of variance. Finding it is the point of the screen." },
        ],
      },
      {
        id: "exceptions",
        heading: "Exceptions",
        blocks: [
          { p: "Everything that does not add up, in one place. Money disbursed against work nobody approved, line items past their accepted date, draws funded without the paperwork that should accompany them, punch items open past their due date." },
          { p: "This is the screen to read before a bank meeting." },
        ],
      },
    ],
  },

  {
    id: "people",
    title: "People",
    summary: "Contractors and subs, messages, document inbox, the contractor agreement.",
    covers: ["parties", "messages", "inbox", "onboarding"],
    sections: [
      {
        id: "parties",
        heading: "Contractors and subs",
        blocks: [
          { p: "Every company on the job. Add a party, then invite a person at that party when you are ready for them to have access." },
          { p: "Parties are never deleted. A party that is no longer working is deactivated, which keeps their history and their signatures intact. A company that briefly existed on a job must not become one that never existed." },
          {
            heading2: "Paperwork",
            p: "Contractors upload their own W-9, licence, insurance certificate and bond from their side. You review and accept each one. Accepting is what sets the compliance fact on the party record, so the record reflects a document somebody actually looked at.",
          },
          { p: "If the job requires bonding, turn that on in Project setup. Scope cannot be offered to a party without a bond on file while that setting is on." },
        ],
      },
      {
        id: "messages",
        heading: "Messages",
        blocks: [
          { p: "Conversations kept with the job. A list on the left, the open thread on the right." },
          { p: "Every message goes out as email as well, with a link back to the thread. Replies belong on the thread, where the rest of the project can see them and where you will look for them later." },
          { p: "A thread can be attached to a line item or a draw. When it is about a specific piece of work, attaching it there keeps the record with the thing it concerns." },
          { note: "A thread is a disclosure as much as a message is, because the participant list names everybody on it. An investor writing to the owner gets a thread containing themselves and the owners and nobody else. Two investors cannot be put on one thread, from either end." },
        ],
      },
      {
        id: "inbox",
        heading: "Document inbox",
        blocks: [
          { p: "Documents that arrived by email and have not been filed yet. The system suggests which draw each one belongs to and how confident it is." },
          { p: "It never files on its own except at the highest confidence, and even then with one tap to undo. A check filed against the wrong draw corrupts the variance on two draws at once." },
        ],
      },
      {
        id: "onboarding",
        heading: "Your agreement",
        blocks: [
          { p: "The screen a contractor sees before they can take work. Eleven clauses, initialled separately, with the time and device recorded against each one." },
          { p: "The clauses cover dates, delay penalties, payment sequence, photographs, retainage, punch items, insurance and licensing, bonding, daily logs, change orders and lien waivers." },
          { p: "This is evidence of understanding, not the contract. The executed agreement is a separate document. When the wording changes the version number moves, and anybody who initialled the previous version has to initial the current one before accepting more scope." },
        ],
      },
    ],
  },

  {
    id: "capital",
    title: "Capital",
    summary: "Funding needs, the investor roster, the briefing, updates, and the boundary around all of it.",
    covers: ["funding", "investors", "investorpage", "updates", "investorview"],
    sections: [
      {
        id: "separate",
        heading: "Why the capital side is kept apart",
        blocks: [
          { p: "Bank draws pay contractors. Investor capital covers carrying costs, equipment, furniture and fittings, inventory and payroll. They are different money with different rules, and mixing them produces reports that are wrong in ways nobody notices until it matters." },
          { p: "Linking a funding need to line items records related work. It is not an earmark, and the system will not treat it as one. Milestones are the honest join between the two sides." },
        ],
      },
      {
        id: "funding",
        heading: "Funding",
        blocks: [
          { p: "What the project needs money for, why, what happens if it does not arrive, and by when." },
          { p: "Three totals are tracked and never merged: interested, committed, and received. Only received counts as raised. Reporting interest as raised money is how a project talks itself into a position it cannot hold." },
        ],
      },
      {
        id: "investors",
        heading: "Investors",
        blocks: [
          { p: "The roster. Sortable by any column, and rows can be selected in bulk for actions like assigning a permission group or releasing held mail." },
          { p: "Importing a roster does not email anybody. Every imported address is held until you release it deliberately, one at a time or in a batch you choose." },
          { note: "No group email ever reveals one investor's address to another. Messages are sent individually, one provider call per recipient, and the code path that would build an address list does not exist." },
        ],
      },
      {
        id: "investorpage",
        heading: "Investor page",
        blocks: [
          { p: "The briefing investors read, authored here and published when it is ready. Until it is published there is nothing for an investor to see, and the draft is not sent to them either." },
          { p: "Documents attached to the briefing are served through short lived signed links, and only to investors on the project." },
        ],
      },
      {
        id: "updates",
        heading: "Investor updates",
        blocks: [
          { p: "Progress notes written for the capital side. Published updates appear on the investor view, in reverse order." },
          { p: "This is the right place to tell every investor something at once. It reaches each of them separately, which a thread cannot do without naming them to each other." },
        ],
      },
      {
        id: "investorview",
        heading: "Investor view",
        blocks: [
          { p: "Exactly what an investor sees, rendered by the same component they get rather than an approximation. Use it to check what you are exposing before you invite somebody." },
          { p: "An investor sees the published briefing, their own commitment, the three capital totals, construction progress as two aggregate numbers, and recent site activity without blockers or names. They can write to the owner. They see no line item, no draw, no contractor, no audit row, and no other investor." },
        ],
      },
    ],
  },

  {
    id: "record",
    title: "The record",
    summary: "The audit trail, and getting data in and out.",
    covers: ["audit", "data"],
    sections: [
      {
        id: "audit",
        heading: "Audit trail",
        blocks: [
          { p: "Every change writes an audit row in the same transaction as the change itself. Not afterwards, and not from a separate process, because a log written separately can be missing the one row that matters." },
          { p: "Rows are chained. Each carries a hash of the row before it, so removing or altering one breaks every row after it and the break is detectable. Update and delete are revoked at the database level for the account the application uses, so the application cannot rewrite its own history even if it tried." },
          { p: "The verify button walks the chain and reports the first row that does not match, or reports nothing, which is what intact looks like." },
        ],
      },
      {
        id: "data",
        heading: "Import and export",
        blocks: [
          { p: "Import a schedule of values from CSV. The columns are the line item number, name, division, scheduled value, and optionally the planned finish date and the party responsible." },
          { p: "Money in the file is read as text and converted once, at the edge. Nothing that decides what is owed touches a floating point number at any point." },
          { p: "Export produces the current schedule of values, the draw history, or the audit trail. Exports are a copy for somebody else to read. The record here stays the original." },
        ],
      },
    ],
  },

  {
    id: "setup",
    title: "Setup and settings",
    summary: "Project setup, settings, and your own account.",
    covers: ["setup", "settings"],
    sections: [
      {
        id: "project-setup",
        heading: "Project setup",
        blocks: [
          { p: "Name, address, timezone, contract sum, contract completion date, retainage percentage, delay penalty per day and its cap, whether a bond is required, and which roles must sign before a line item can be released." },
          { p: "The timezone is money logic, not a display preference. Penalties accrue per calendar day at the site, so the day boundary decides the amount." },
          { note: "Retainage and the delay penalty have to be confirmed before a payment application can be prepared. Saving this screen once confirms them, whatever values you save. Zero retainage is a real contract; what the system refuses is a first draw built on defaults nobody read." },
        ],
      },
      {
        id: "settings",
        heading: "Settings",
        blocks: [
          { p: "Notification preferences, the digest, and the project's reply address for outgoing mail." },
          { p: "Held addresses are managed here as well. An address is held when it hard bounced, when somebody complained, or when it was imported and not yet released. Releasing an address is a decision and it is recorded." },
        ],
      },
      {
        id: "account",
        heading: "Your account",
        blocks: [
          { p: "Reachable from your name in the top right corner, from any screen. Change your password, and update your own contact details." },
          { p: "Your email address is not editable there. It is the address you were invited at and the one your record is kept under. Ask the developer if it needs to change." },
          { p: "Changing your password ends every other session on your account and keeps the one you are using." },
        ],
      },
    ],
  },

  {
    id: "email",
    title: "How email works",
    summary: "What gets sent, to whom, and what stops a message going out.",
    sections: [
      {
        id: "what-sends",
        heading: "What sends mail",
        blocks: [
          { p: "Invitations, draw notifications, punch items raised and waived, change orders, thread messages and investor updates. Each one is recorded as a row per recipient before anything leaves, so the record of what was sent does not depend on the provider." },
        ],
      },
      {
        id: "one-each",
        heading: "One message per person",
        blocks: [
          { p: "Recipients are never combined. A message going to five people is five separate messages, each carrying one address. There is no carbon copy field anywhere in the system." },
          { p: "The body of a notification names the author and nobody else. A thread notification tells you who wrote and links to the thread, and the thread itself is where the participant list lives." },
        ],
      },
      {
        id: "held",
        heading: "Why a message might not have gone",
        blocks: [
          {
            list: [
              "The address is held. Hard bounces and complaints hold an address automatically. Imported rosters are held until released.",
              "The whole domain is held. Reserved and fixture domains cannot resolve, so every message to one is a guaranteed bounce against the sending reputation of the domain that also carries your draw notices.",
              "The project has not finished setup.",
            ],
          },
          { p: "A soft bounce, such as a full mailbox, does not hold an address. Today's full inbox is not a reason to stop writing to the bank forever." },
        ],
      },
    ],
  },

  {
    id: "evidence",
    title: "Photos and documents",
    summary: "How files are stored, and why they are stored that way.",
    sections: [
      {
        id: "originals",
        heading: "Originals are kept untouched",
        blocks: [
          { p: "The bytes that arrive are the bytes that are stored. A SHA-256 hash is recorded at upload, EXIF is preserved, and thumbnails are derived separately rather than by re-encoding the original." },
          { p: "A scanned check is evidence. Re-compressing it to save space weakens it as evidence, which is the wrong trade at any price." },
        ],
      },
      {
        id: "access",
        heading: "Getting to a file",
        blocks: [
          { p: "Downloads go through short lived signed links, issued only after your access to the record behind the file has been checked. A link is good for a few minutes and cannot be shared usefully after that." },
          { p: "Nothing is deleted. Documents are soft deleted, which keeps the fact that they existed." },
        ],
      },
    ],
  },
  /* ------------------------------------------------ guides by role ------- */

  {
    id: "guide-contractor",
    title: "Guide for contractors",
    audience: "contractor",
    summary: "Taking scope, starting work, getting paid, and what will refuse you.",
    sections: [
      {
        id: "c-before",
        heading: "Before you can take work",
        blocks: [
          { p: "Two things have to happen, in this order." },
          {
            steps: [
              "Initial the agreement. Eleven clauses, each initialled separately, on the screen called Your agreement. It records the time and the device against each one.",
              "Upload your paperwork. W-9, licence, insurance certificate, and a bond if the job requires one. Upload them yourself from your own screen. The developer reviews and accepts each.",
            ],
          },
          { p: "Until both are done, scope cannot be offered to you. If the clause wording changes later, the version moves and you initial the current version before accepting anything more. Work already accepted is unaffected." },
        ],
      },
      {
        id: "c-scope",
        heading: "Accepting scope",
        blocks: [
          { p: "An offer names the line item, its value, its finish date and the delay penalty rate. Accepting it freezes those four things against your name." },
          { p: "Read the finish date before you accept it. Accepting is a statement that you looked at that date and can hit it. Delay penalties only ever accrue against a date you accepted, which is exactly why the date matters at the moment you agree to it and not afterwards." },
          { p: "Declining is always available and costs nothing. Say so before you accept rather than after." },
        ],
      },
      {
        id: "c-doing",
        heading: "Doing the work",
        blocks: [
          {
            table: {
              head: ["Step", "What is required"],
              rows: [
                ["Start", "At least one photograph. The system will not let you start without it."],
                ["Update progress", "Set value in place as you go. This is the only field on a line item you can change."],
                ["Submit", "At least one more photograph, taken on completion."],
              ],
            },
          },
          { p: "Shoot from the same position each time. Two photographs from the same spot are worth more than ten from wherever you happened to stand." },
          { p: "Post a daily log on days you work. Crew, hours, what happened, anything blocking you. It takes a minute and it is what makes a delay conversation survivable months later." },
        ],
      },
      {
        id: "c-paid",
        heading: "Getting paid",
        blocks: [
          { p: "Submitting is not the last step. After you submit, the inspector verifies, the developer approves, and every required signature has to be on file before the item is released. Only released work goes into a payment application." },
          { p: "What reaches you is value in place, less retainage, less what you have already been paid, less any delay penalties, less anything held back against an open punch item." },
          { p: "Lien waivers accompany each payment. That is one of the clauses you initial." },
        ],
      },
      {
        id: "c-refused",
        heading: "What will refuse you, and why",
        blocks: [
          {
            list: [
              "Starting without a photograph. The gate is the point.",
              "Submitting without a completion photograph.",
              "Editing anything except percent on your own line items. Every other field is rejected and the response says which.",
              "Touching a line item held by another party.",
              "Accepting scope before you have initialled the current agreement.",
              "Accepting scope without a bond on file, when the job requires bonding.",
            ],
          },
          { p: "You see the scope you hold and nothing else. Other subs' rates, the contract sum and the draw history are not on your screen." },
        ],
      },
    ],
  },

  {
    id: "guide-inspector",
    title: "Guide for inspectors",
    audience: "inspector",
    summary: "Verifying work, raising punch items, and the line you do not cross.",
    sections: [
      {
        id: "i-role",
        heading: "What verification means here",
        blocks: [
          { p: "Verification is a statement that the work described has been done to the standard the job requires. It sits between the contractor submitting and the developer approving, and both of those are separate decisions made by other people." },
          { p: "Inspection is not approval. You cannot approve work and you cannot release money. That separation is the reason your signature is worth anything." },
        ],
      },
      {
        id: "i-punch",
        heading: "Raising punch items",
        blocks: [
          { p: "Anything not right gets raised against the line item it belongs to. Choose the severity honestly, because severity decides what happens to money." },
          {
            table: {
              head: ["Severity", "What it does"],
              rows: [
                ["Blocking", "Nobody can verify, approve or release that line item until it is resolved."],
                ["Holdback", "A named amount is withheld on top of retainage until you verify the fix."],
                ["Minor", "Recorded on the item. No effect on payment."],
              ],
            },
          },
          { p: "Photograph the deficiency. A punch item with a picture is closed faster than one without." },
        ],
      },
      {
        id: "i-see",
        heading: "What you see",
        blocks: [
          { p: "The whole job, not one contractor's slice: the full schedule of values, the timeline, the daily logs and the punch list. You need the whole picture to judge one part of it." },
          { p: "You do not release funds and you do not change scope or commercial terms." },
        ],
      },
    ],
  },

  {
    id: "guide-lender",
    title: "Guide for lenders",
    audience: "lender",
    summary: "Receiving draw requests, checking them, funding, and closing the file.",
    sections: [
      {
        id: "l-arrives",
        heading: "When a draw arrives",
        blocks: [
          { p: "You are notified when a payment application is submitted. It arrives with the line items it covers, the photographs behind each one, the signatures already on file, and the arithmetic." },
          { p: "Net due is value in place, less retainage, less previously paid, less delay penalties, less open holdbacks. Every component is shown separately rather than as one number you have to take on trust." },
          { p: "The figures are frozen at submission. If work continues after the request goes out, this application still says what it said when it was made." },
        ],
      },
      {
        id: "l-check",
        heading: "What to check before funding",
        blocks: [
          {
            list: [
              "Every line item in the request is released, meaning every required signature is on file.",
              "The photographs match the value being claimed.",
              "The Exceptions screen is clear, or the exceptions on it are ones you accept.",
              "Budget vs schedule of values reconciles, or the variance is understood.",
            ],
          },
          { p: "You can see the whole job. You cannot start work, verify it or approve it, because a lender approving the work they are funding is the conflict this separation exists to prevent." },
        ],
      },
      {
        id: "l-fund",
        heading: "Funding, returning, and short paying",
        blocks: [
          { p: "Release the draw to fund it, or return it with a reason. A returned draw goes back with your note attached, and nothing is lost." },
          { p: "Short paying is supported and expected. Enter the amount actually paid, and the difference is reconciled against the frozen snapshot. Payment is allocated across the line items by largest remainder, so the parts sum to what you sent, exactly." },
        ],
      },
      {
        id: "l-docs",
        heading: "Closing the file",
        blocks: [
          { p: "Attach proof of payment: the check copy, the wire confirmation, the lien waivers. Upload the original file. It is stored byte for byte with its hash recorded, because a scanned check is evidence rather than a picture of one." },
          { p: "A draw funded without its paperwork appears on the Exceptions screen until the file is complete. Money moved and the record is incomplete is a state worth surfacing rather than tolerating." },
        ],
      },
    ],
  },

  {
    id: "guide-investor",
    title: "Guide for investors",
    audience: "investor",
    summary: "What you can see, what you deliberately cannot, and how to ask a question.",
    sections: [
      {
        id: "v-see",
        heading: "What you see",
        blocks: [
          {
            list: [
              "The briefing the developer publishes, with any documents attached to it.",
              "Where your own commitment stands.",
              "Three totals for the round: interested, committed, and received. Only received counts as raised.",
              "Construction progress as two numbers, the contract value and the value built to date.",
              "Recent site activity, summarised.",
            ],
          },
          { p: "Your own record is shown to you, and you can correct your phone number and contact details from it." },
        ],
      },
      {
        id: "v-cannot",
        heading: "What you do not see, and why",
        blocks: [
          { p: "No line item, no draw, no contractor, no punch item, no penalty and no audit row. Construction detail read at half resolution produces alarm that somebody then has to answer, and the answer is almost always that the number means something other than it appears to." },
          { p: "You also do not see the other investors. Not their names, not their amounts, not their addresses. That works in both directions: they do not see you either." },
        ],
      },
      {
        id: "v-ask",
        heading: "Asking a question",
        blocks: [
          { p: "At the foot of the briefing there is a channel to the developer. Write there and it reaches them by email, their answer comes back to that page, and the exchange is kept as part of the project record." },
          { p: "It goes to the developer and to nobody else. No other investor sees the message, or sees that you sent one. A thread of yours can only ever contain you and the developer, which is enforced rather than remembered." },
          { p: "You will get an email when there is an answer, with a link straight to the conversation." },
        ],
      },
      {
        id: "v-money",
        heading: "About the money",
        blocks: [
          { p: "Contributions are made to the entity and applied at the managing member's discretion. Directing a contribution toward a particular need records intent and priority. It is not an earmark, and the system does not present it as one." },
        ],
      },
    ],
  },

  {
    id: "guide-partner",
    title: "Guide for partners and staff",
    audience: "partner",
    summary: "Working alongside the developer with only the capabilities you were given.",
    sections: [
      {
        id: "p-start",
        heading: "What you start with",
        blocks: [
          { p: "Visibility, and nothing that changes anything. You can read the job in full while every write is refused until the developer grants it." },
          { p: "This is deliberate. Adding somebody to a project should not be a decision about how much damage they can do on their first day." },
        ],
      },
      {
        id: "p-grants",
        heading: "Capabilities you can be given",
        blocks: [
          { p: "Grants are additive and made one at a time by the developer, on the Contractors and subs screen. Each is recorded against your name." },
          { p: "Typical ones are raising punch items, posting daily logs, managing suppliers, managing parties, publishing investor updates and handling capital." },
        ],
      },
      {
        id: "p-never",
        heading: "What cannot be delegated",
        blocks: [
          { p: "Releasing funds, marking a draw paid and changing project settings stay with the roles that hold them, however many grants you have. These are not oversights waiting to be fixed. Some decisions belong to the person who is accountable for them, and delegating them quietly is how accountability disappears." },
        ],
      },
    ],
  },
];
